/**
 * Globalny serwis modalnych komunikatów dla użytkownika.
 *
 * Zastępuje natywne okno `alert()` dużym, wyśrodkowanym oknem modalnym,
 * które nie znika samoczynnie i wymaga potwierdzenia przez użytkownika.
 *
 * Może być wywoływany spoza drzewa Reacta (np. z klas serwisowych),
 * ponieważ działa jako moduł-singleton z mechanizmem subskrypcji.
 */

export type MessageDialogVariant = 'danger' | 'warning' | 'info';

export interface MessageDialogState {
  title: string;
  message: string;
  variant?: MessageDialogVariant;
}

type Listener = (state: MessageDialogState | null) => void;

let current: MessageDialogState | null = null;
const queue: MessageDialogState[] = [];
const listeners = new Set<Listener>();

const emit = () => {
  listeners.forEach(listener => listener(current));
};

const isDuplicate = (state: MessageDialogState) => {
  const sameAs = (other: MessageDialogState | null) =>
    other?.title === state.title && other?.message === state.message;
  return sameAs(current) || queue.some(sameAs);
};

/**
 * Wyświetla modalny komunikat. Jeśli inny komunikat jest już widoczny,
 * nowy trafia do kolejki i zostanie pokazany po zamknięciu poprzedniego.
 * Identyczne komunikaty są pomijane.
 */
export const showMessageDialog = (state: MessageDialogState): void => {
  if (isDuplicate(state)) {
    return;
  }
  if (current) {
    queue.push(state);
    return;
  }
  current = state;
  emit();
};

/** Zamyka bieżący komunikat i wyświetla kolejny z kolejki (jeśli istnieje). */
export const closeMessageDialog = (): void => {
  current = queue.shift() ?? null;
  emit();
};

/** Subskrypcja stanu używana przez komponent modalny. */
export const subscribeMessageDialog = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener(current);
  return () => {
    listeners.delete(listener);
  };
};
